/**
 * The trainer's links on the public page: their social profiles, their site, an address and a phone
 * number — what each kind accepts, where the row is drawn, and the `href` each one opens.
 *
 * ── The page never draws an address that does not pass ──────────────────────────────────────
 * The server refuses a bad address on the save, but the page reads the stored document as
 * untrusted data (`parseAppearance`), so the same rule is applied again where a link is drawn: a
 * row that fails `linkIssue` is simply not there. The builder applies it as the trainer types, row
 * by row, so the save is never the first to say so.
 *
 * ── The backend's rules, mirrored (`AppearanceRules.Links.cs`) ───────────────────────────────
 * A web address is `https`, at most 300 characters, without spaces or credentials, to a named
 * public host (two labels or more, a top-level domain that is not all digits nor one of the names a
 * private network or nothing resolves — `PRIVATE_TOP_LEVELS`, the backend's `PrivateTopLevels`). A
 * social kind is its platform's own host, bare or with `www.` — `whatsapp` is `wa.me` alone. An
 * e-mail is a plain address (opened as `mailto:`); a phone is digits, spaces and `-` with an
 * optional leading `+`, five to fifteen digits (opened as `tel:`). The server stores a web address
 * as its absolute form (host lower-cased, a bare host given its `/`), which the next read returns.
 */

export type StudioLinkKind =
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'linkedin'
  | 'x'
  | 'whatsapp'
  | 'website'
  | 'email'
  | 'phone';

export const LINK_KINDS: readonly StudioLinkKind[] = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'linkedin',
  'x',
  'whatsapp',
  'website',
  'email',
  'phone',
];

/** `start`: the first page (the welcome, or who-you-are when it is skipped). `end`: the confirmation. */
export type StudioLinkPlacement = 'end' | 'start' | 'both';

export const LINK_PLACEMENTS: readonly StudioLinkPlacement[] = ['end', 'start', 'both'];

export const MAX_LINKS = 5;

/** The longest web address a row takes; the backend's own cap (`LinkMaxLength`). */
export const LINK_URL_MAX = 300;

export interface StudioLink {
  readonly kind: StudioLinkKind;
  /** As typed: an `https` address, a plain e-mail address or a phone number, by kind. */
  readonly url: string;
}

/** Why a row cannot be drawn, or saved. */
export type LinkIssue = 'empty' | 'tooLong' | 'https' | 'host' | 'email' | 'phone';

const PLATFORM_HOSTS: Readonly<Record<Exclude<StudioLinkKind, 'website' | 'email' | 'phone'>, readonly string[]>> = {
  instagram: ['instagram.com'],
  facebook: ['facebook.com', 'fb.com'],
  tiktok: ['tiktok.com'],
  youtube: ['youtube.com', 'youtu.be'],
  linkedin: ['linkedin.com'],
  x: ['x.com', 'twitter.com'],
  whatsapp: ['wa.me'],
};

// Mirrors the backend: nothing a mailto: URL reads as a header or an escape (? & = % # /).
const EMAIL = /^[^\s@<>()[\]:;,"?&=%#/]+@[^\s@<>()[\]:;,"?&=%#/]+\.[^\s@<>()[\]:;,".?&=%#/]+$/;
const EMAIL_MAX = 254;
const PHONE = /^\+?[0-9][0-9 -]*$/;
const PHONE_MAX = 30;
const PHONE_MIN_DIGITS = 5;
const PHONE_MAX_DIGITS = 15;
// eslint-disable-next-line no-control-regex -- the rule IS about control characters: the backend refuses any.
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * Top-level names that resolve only inside somebody's network, or nowhere: reserved and special-use
 * names and the ones private networks use by habit. A link to one would lead a filler's browser at
 * their own router or intranet. The backend's list, word for word.
 */
const PRIVATE_TOP_LEVELS: ReadonlySet<string> = new Set([
  'localhost',
  'local',
  'localdomain',
  'internal',
  'intranet',
  'private',
  'lan',
  'home',
  'corp',
  'test',
  'example',
  'invalid',
  'onion',
  'alt',
  'arpa',
]);

/** The `https` address's host, when it names a public host — else what is wrong with it. */
function publicHttpsHost(value: string): { readonly host: string } | { readonly issue: 'https' | 'host' } {
  if (/\s/.test(value)) return { issue: 'host' };
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { issue: 'https' };
  }
  if (url.protocol !== 'https:') return { issue: 'https' };
  if (url.username !== '' || url.password !== '') return { issue: 'host' };
  // The browser's URL parser forgives what the backend's refuses (`https:\\host`, `https:/host`,
  // full-width host letters); insist on the plain spelling so a row the builder accepts saves.
  if (!/^https:\/\/[a-z0-9.-]+(:\d+)?([/?#]|$)/i.test(value)) return { issue: 'https' };
  const host = url.hostname.toLowerCase();
  const labels = host.split('.');
  const topLevel = labels[labels.length - 1] ?? '';
  const named =
    !host.startsWith('[') &&
    labels.length >= 2 &&
    topLevel !== '' &&
    !PRIVATE_TOP_LEVELS.has(topLevel) &&
    !/^\d+$/.test(topLevel);
  return named ? { host } : { issue: 'host' };
}

/** What is wrong with a row, or `null` when it may be saved and drawn. */
export function linkIssue(link: StudioLink): LinkIssue | null {
  const value = link.url.trim();
  if (value === '') return 'empty';
  switch (link.kind) {
    case 'email':
      return value.length <= EMAIL_MAX && EMAIL.test(value) ? null : 'email';
    case 'phone': {
      const digits = value.replace(/\D/g, '').length;
      return value.length <= PHONE_MAX && PHONE.test(value) && digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS
        ? null
        : 'phone';
    }
    default: {
      if (CONTROL.test(value)) return 'host';
      if (value.length > LINK_URL_MAX) return 'tooLong';
      const read = publicHttpsHost(value);
      if ('issue' in read) return read.issue;
      if (link.kind === 'website') return null;
      return PLATFORM_HOSTS[link.kind].some((domain) => read.host === domain || read.host === `www.${domain}`)
        ? null
        : 'host';
    }
  }
}

/** What a row opens, or `null` when it does not pass — and is then not drawn at all. */
export function linkHref(link: StudioLink): string | null {
  if (linkIssue(link) !== null) return null;
  const value = link.url.trim();
  if (link.kind === 'email') return `mailto:${value}`;
  if (link.kind === 'phone') return `tel:${value.startsWith('+') ? '+' : ''}${value.replace(/\D/g, '')}`;
  return value;
}

export interface DrawnLink {
  readonly kind: StudioLinkKind;
  readonly href: string;
  /** Opened in a new tab: every address but `mailto:` and `tel:`, which hand over to an app. */
  readonly external: boolean;
}

/**
 * The links drawn on one of the page's two ends, in the trainer's order: only the rows that pass,
 * and only on the end the placement names. `first` is the page a filler opens on.
 */
export function linksAt(
  links: { readonly placement: StudioLinkPlacement; readonly items: readonly StudioLink[] },
  end: 'first' | 'last',
): readonly DrawnLink[] {
  const shown = links.placement === 'both' || (end === 'first' ? links.placement === 'start' : links.placement === 'end');
  if (!shown) return [];
  return links.items.flatMap((link): DrawnLink[] => {
    const href = linkHref(link);
    return href === null ? [] : [{ kind: link.kind, href, external: link.kind !== 'email' && link.kind !== 'phone' }];
  });
}
